import axios from "axios";
import { injectable } from "inversify";
import "reflect-metadata";
import {
  ContainerResponse,
  LoginResponse,
  MetaData,
  NotificationResponse,
  PaginationCollection,
  PositionResponse,
  PublicPositionResponse,
  SendMessageRequest,
} from "@/models/dtos";
import {
  IEmploymentService,
  IEmploymentHttpClient,
} from "@/interfaces/IEmploymentService";
import { IJobPositionResource } from "@/interfaces/IJobPositionResource";
import { IContainerResource } from "@/interfaces/IContainerResource";
import { TYPES } from "@/types";

import { useCookies } from "@vueuse/integrations/useCookies";

@injectable()
export class EmploymentHttpClient implements IEmploymentHttpClient {
  private client: any;
  constructor() {
    this.client = this.createClient("", true);
  }

  createClient(token: string, includeAuthorization: boolean): any {
    const headers: any = {
      "Content-Type": "application/json",
    };

    if (includeAuthorization) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const _axios = axios.create({
      baseURL: TYPES.BaseURL,
      headers: headers,
    });
    _axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("user_token");

        if (token) {
          config.headers["Authorization"] = "Bearer " + token;
        }
        config.headers["Content-Type"] = "application/json";
        return config;
      },
      (error) => {
        Promise.reject(error);
      }
    );

    return _axios;
  }

  updateClient(token: string): void {
    this.client = this.createClient(token, true);
  }

  async getUrl(url: string): Promise<any> {
    return this.client.get(url);
  }
  async postUrl(url: string, data: any): Promise<any> {
    return this.client.post(url, data);
  }
  async post(data: any): Promise<any> {
    return this.client.post("/api/dispatcher", data);
  }
}
@injectable()
export class EmploymentService implements IEmploymentService {
  private _username = "";
  private _password = "";
  private _login: LoginResponse = new LoginResponse();

  JobPosition: IJobPositionResource;
  Container: IContainerResource;

  constructor(
    positionResouse: IJobPositionResource,
    containerResource: IContainerResource,
    private httpClient: IEmploymentHttpClient
  ) {
    this.JobPosition = positionResouse;
    this.Container = containerResource;
  }
  async deleteNotificationById(id: string, containerId: number): Promise<any> {
    const data = {
      action: "delete_NotificationById",
      data: {
        containerId: containerId,
        id: id,
      },
    };
    return await this.httpClient.post(data);
  }
  async sendMessage(v: SendMessageRequest): Promise<any[]> {
    const data = {
      action: "send_MessageCommand",
      data: v,
    };
    return await this.httpClient.post(data);
  }

  async getGetMessagingChannelsQuery(
    containerId: number
  ): Promise<NotificationResponse[]> {
    const data = {
      action: "get_MessagingChannels",
      data: {
        containerId: containerId,
      },
    };
    return await this.httpClient.post(data);
  }

  async createApplicant(email: string, positionId: number, metadata: MetaData) {
    const data = {
      action: "create_jobApplication",
      data: {
        email: email,
        positionId: positionId,
        metadata: metadata,
      },
    };
    return await this.httpClient.postUrl(
      TYPES.BaseURL + "/api/public/positions/apply",
      data
    );
  }

  async getPublicPositions(
    take: number,
    page: number,
    containerId?: number
  ): Promise<PaginationCollection<PositionResponse[]>> {
    const url = containerId
      ? `/api/public/positions?containerId=${containerId}&take=${take}&page=${page}`
      : `/api/public/positions?take=${take}&page=${page}`;

    const response = await this.httpClient.getUrl(TYPES.BaseURL + url);
    return response.data;
  }
  async getPublicPosition(
    id: number | string
  ): Promise<PublicPositionResponse> {
    const response = await this.httpClient.getUrl(
      TYPES.BaseURL + "/api/public/positions/" + id
    );
    return response.data;
  }
  async getContainerByName(name: string): Promise<ContainerResponse> {
    const response = await this.httpClient.getUrl(
      TYPES.BaseURL + "/api/public/containers/" + name
    );
    return response.data;
  }
  async getContainerByPositionId(id: number): Promise<ContainerResponse> {
    const response = await this.httpClient.getUrl(
      TYPES.BaseURL + "/api/public/open/" + id + "/container"
    );
    return response.data;
  }
  async login(username: string, password: string): Promise<LoginResponse> {
    this._username = username;
    this._password = password;

    const url = "/api/account/login";
    const model = {
      email: this._username,
      password: this._password,
    };

    const tempClient = new EmploymentHttpClient().createClient("", false);
    const response = await tempClient.post(url, model);
    localStorage.setItem("user_token", response.data.token);
    const loginResponse: LoginResponse = response.data;
    this.httpClient.updateClient(loginResponse.token);
    return loginResponse;
  }
}
